/**
 * Professional date and time formatting utilities
 */

export interface FormattedDateTime {
  primary: string;
  secondary?: string;
  relative: string;
}

/**
 * Check if date is today
 */
const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Check if date is yesterday
 */
const isYesterday = (date: Date): boolean => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
};

/**
 * Check if date is this week
 */
const isThisWeek = (date: Date): boolean => {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return date >= weekAgo;
};

/**
 * Check if date is this year
 */
const isThisYear = (date: Date): boolean => {
  const now = new Date();
  return date.getFullYear() === now.getFullYear();
};

/**
 * Format time as 12-hour format
 */
const formatTime = (date: Date): string => {
  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get day name
 */
const getDayName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

/**
 * Get relative time string
 */
const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;

  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
};

/**
 * Formats a date in a professional, user-friendly way
 * @param date - The date to format
 * @returns Formatted date string
 */
export const formatLastModified = (date: Date): FormattedDateTime => {
  if (isToday(date)) {
    return {
      primary: formatTime(date),
      secondary: 'Today',
      relative: getRelativeTime(date)
    };
  }

  if (isYesterday(date)) {
    return {
      primary: formatTime(date),
      secondary: 'Yesterday',
      relative: getRelativeTime(date)
    };
  }

  if (isThisWeek(date)) {
    return {
      primary: getDayName(date),
      secondary: formatTime(date),
      relative: getRelativeTime(date)
    };
  }

  if (isThisYear(date)) {
    return {
      primary: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      secondary: formatTime(date),
      relative: getRelativeTime(date)
    };
  }

  return {
    primary: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    secondary: formatTime(date),
    relative: getRelativeTime(date)
  };
};

/**
 * Simple professional date format for tables
 * @param date - The date to format
 * @returns Simple formatted date string
 */
export const formatTableDate = (date: Date): string => {
  if (isToday(date)) {
    return `Today, ${formatTime(date)}`;
  }

  if (isYesterday(date)) {
    return `Yesterday, ${formatTime(date)}`;
  }

  if (isThisWeek(date)) {
    return `${getDayName(date)}, ${formatTime(date)}`;
  }

  if (isThisYear(date)) {
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${formatTime(date)}`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Relative time format (e.g., "2 hours ago")
 * @param date - The date to format
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date): string => {
  return getRelativeTime(date);
};